/**
 * Store for managing pre-approved member emails
 *
 * This store handles the functionality for captains to pre-approve emails
 * for joining their SubKrewes, creating a more welcoming experience for
 * expected members.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import type { PreapprovedMember } from '~/shared/types/preapprovedMember.js'

export const usePreapprovedMembersStore = defineStore('preapprovedMembers', {
  state: () => ({
    preapprovedMembers: {} as Record<string, PreapprovedMember>,
    loaded: {} as Record<string, boolean>, // Track which SubKrewes we've loaded
  }),

  getters: {
    /**
     * Get pre-approved members for a specific SubKrewe
     */
    getPreapprovedForSubKrewe(): (subkreweId: string) => PreapprovedMember[] {
      return (subkreweId: string) => {
        return Object.values(this.preapprovedMembers).filter(
          member => member.subkreweId === subkreweId,
        )
      }
    },
  },

  actions: {
    /**
     * Check if an email is pre-approved for a specific SubKrewe
     */
    isEmailPreapproved(subkreweId: string, email: string): boolean {
      // Make sure we have loaded the data for this SubKrewe
      if (!this.loaded[subkreweId]) {
        console.warn('Pre-approved members not loaded for this SubKrewe')
        return false
      }

      // Normalize the email for comparison
      const normalizedEmail = email.trim().toLowerCase()

      // Check if there's a matching unused pre-approval
      return Object.values(this.preapprovedMembers).some(
        member =>
          member.subkreweId === subkreweId
          && member.email === normalizedEmail,
      )
    },

    /**
     * Add multiple pre-approved emails for a SubKrewe
     * @param subkreweId The SubKrewe ID
     * @param emailsText Text with one email per line
     * @returns Number of emails successfully added
     */
    async addBulkPreapprovedMembers(subkreweId: string, emailsText: string): Promise<number> {
      try {
        if (!subkreweId || !emailsText.trim()) {
          return 0
        }

        const db = useFirestore()
        const currentUser = useCurrentUser()

        if (!currentUser.value?.uid) {
          console.error('User not authenticated')
          return 0
        }

        // Store the authenticated user to help TypeScript understand it's safe to use
        const authenticatedUser = currentUser.value

        // Parse emails (one per line), normalize, and remove duplicates
        const rawEmails = emailsText.split(/[\n,]/)
          .map(email => email.trim().toLowerCase())
          .filter(email => email.length > 0 && email.includes('@'))

        // Remove duplicates
        const uniqueEmails = [...new Set(rawEmails)]

        // Create a batch of promises to add each email
        const promises = uniqueEmails.map(async (email) => {
          // Don't add if this email is already pre-approved for this SubKrewe
          const existing = Object.values(this.preapprovedMembers).find(
            m => m.subkreweId === subkreweId && m.email === email,
          )

          if (existing) {
            return null // Skip this one
          }

          // Add the new pre-approval
          const preapprovalData = {
            subkreweId,
            email,
            addedBy: authenticatedUser.uid,
            addedAt: serverTimestamp(),
            used: false,
          }

          const docRef = await addDoc(collection(db, 'preapproved_members'), preapprovalData)

          // Add to local state
          const newPreapproval: PreapprovedMember = {
            id: docRef.id,
            ...preapprovalData,
            addedAt: new Date(),
          }

          this.preapprovedMembers[docRef.id] = newPreapproval
          return newPreapproval
        })

        // Wait for all operations to complete
        const results = await Promise.all(promises)

        // Mark this SubKrewe as loaded
        this.loaded[subkreweId] = true

        // Return count of successfully added emails
        return results.filter(r => r !== null).length
      }
      catch (error) {
        console.error('Error adding pre-approved members:', error)
        return 0
      }
    },

    /**
     * Load pre-approved members for a specific SubKrewe
     */
    async loadForSubKrewe(subkreweId: string): Promise<void> {
      try {
        const db = useFirestore()

        // Query for pre-approved members for this SubKrewe
        const q = query(
          collection(db, 'preapproved_members'),
          where('subkreweId', '==', subkreweId),
        )

        const querySnapshot = await getDocs(q)

        // Process results
        querySnapshot.forEach((doc) => {
          const data = doc.data()

          // Convert server timestamp to Date if needed
          const addedAt = data.addedAt
            ? (data.addedAt.toDate ? data.addedAt.toDate() : data.addedAt)
            : new Date()

          // Create the PreapprovedMember object
          const preapproval: PreapprovedMember = {
            id: doc.id,
            subkreweId: data.subkreweId,
            email: data.email,
            addedBy: data.addedBy,
            addedAt,

          }

          // Add to state
          this.preapprovedMembers[doc.id] = preapproval
        })

        // Mark this SubKrewe as loaded
        this.loaded[subkreweId] = true
      }
      catch (error) {
        console.error(`Error loading pre-approved members for SubKrewe ${subkreweId}:`, error)
      }
    },

    /**
     * Delete a pre-approval
     */
    async deletePreapproval(preapprovalId: string): Promise<boolean> {
      try {
        const db = useFirestore()

        // Delete from Firestore
        const preapprovalRef = doc(db, 'preapproved_members', preapprovalId)
        await deleteDoc(preapprovalRef)

        // Remove from state
        if (this.preapprovedMembers[preapprovalId]) {
          const { subkreweId } = this.preapprovedMembers[preapprovalId]
          delete this.preapprovedMembers[preapprovalId]

          // Make sure we keep track that we've loaded this SubKrewe
          this.loaded[subkreweId] = true
        }

        return true
      }
      catch (error) {
        console.error(`Error deleting pre-approval ${preapprovalId}:`, error)
        return false
      }
    },

    /**
     * Check if a user's email is pre-approved for any SubKrewe
     * This is useful for the sign-up flow to direct users to their SubKrewes
     */
    async findPreapprovalsByEmail(email: string): Promise<PreapprovedMember[]> {
      try {
        const db = useFirestore()
        const normalizedEmail = email.trim().toLowerCase()

        // Query for pre-approved members with this email
        const q = query(
          collection(db, 'preapproved_members'),
          where('email', '==', normalizedEmail),
          where('used', '==', false),
        )

        const querySnapshot = await getDocs(q)
        const results: PreapprovedMember[] = []

        // Process results
        querySnapshot.forEach((doc) => {
          const data = doc.data()

          // Convert server timestamp to Date if needed
          const addedAt = data.addedAt
            ? (data.addedAt.toDate ? data.addedAt.toDate() : data.addedAt)
            : new Date()

          // Create the PreapprovedMember object
          const preapproval: PreapprovedMember = {
            id: doc.id,
            subkreweId: data.subkreweId,
            email: data.email,
            addedBy: data.addedBy,
            addedAt,
          }

          // Add to results and state
          results.push(preapproval)
          this.preapprovedMembers[doc.id] = preapproval

          // Mark this SubKrewe as loaded
          this.loaded[data.subkreweId] = true
        })

        return results
      }
      catch (error) {
        console.error(`Error finding pre-approvals for email ${email}:`, error)
        return []
      }
    },
  },
})
